// Drawola Studio — "Export as .drawola file". Fork-only.
//
// Reuses upstream's canonical scene serializer (`serializeAsJSON(..., "local")`
// — the exact call behind main-menu "Save to disk"), so a `.drawola` file is a
// byte-for-byte valid `.excalidraw` document; only the extension and MIME type
// carry the fork's branding (see the PWA manifest entries in
// excalidraw-app/vite.config.mts).
//
// We do not go through upstream's `fileSave()` / browser-fs-access: that
// resolves a `FileSystemFileHandle` meant to be stored on the editor's
// `appState.fileHandle` for later "Save to active file" writes. The Studio has
// no editor state to attach a handle to, so a plain anchor download is both
// simpler and side-effect free.

import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";

import { getDoc } from "../storage/multiFileStore";

import { toFileName } from "./format";

/** Matches `"application/vnd.drawola+json"` from the PWA manifest. */
const DRAWOLA_MIME_TYPE = "application/vnd.drawola+json";
const DRAWOLA_EXTENSION = "drawola";

/**
 * Serialize a stored doc and hand it to the browser as a download.
 *
 * @param docId   doc to export
 * @param fallbackName used when the doc name has no filename-safe characters
 * @returns `false` when the doc no longer exists
 */
export const exportDocToFile = async (
  docId: string,
  fallbackName: string,
): Promise<boolean> => {
  const doc = await getDoc(docId);
  if (!doc) {
    return false;
  }

  const serialized = serializeAsJSON(
    doc.elements,
    doc.appState,
    doc.files,
    "local",
  );
  const blob = new Blob([serialized], { type: DRAWOLA_MIME_TYPE });
  const url = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${toFileName(
      doc.name,
      fallbackName,
    )}.${DRAWOLA_EXTENSION}`;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Give the browser a tick to start the transfer before revoking.
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  return true;
};
